import React, { Component } from "react";
import * as Sentry from "@sentry/react";
import ScrollToTop from "../../components/utils/ScrollToTop";
import Action from "../../components/Action";
import Card from "../../components/DashboardCard";
import Confirmation from "../../components/Confirmation";
import QuestionDescription from "../../components/QuestionDescription";
import PageHeader from "../../components/PageHeader";
import Image from "../../components/Image";
import imageOfficeWorking from "../../images/office-working.png";
import FileUploadQuestion from "../../components/FileUploadQuestion";
import { sanitiseHtml } from "../../utils/sanitiseHtml";

/**
 * Deprecated. case_document steps replace this component for new information requests;
 * the adapter maps them to document_upload for renderers. Kept while the staff app still
 * creates document_request doables and for completed ones.
 */
class DocumentUploadDoable extends Component {
  constructor(props) {
    super(props);
    this.state = {
      documents: props.doable.configuration.documents.reduce((docs, doc) => {
        docs[doc.identifier] = [];
        return docs;
      }, {}),
      documentsUploading: {},
      errors: {},
      errorMessages: {},
      error: null,
      isDuplicateSubmission: false,
      submitted: null,
      submitting: false,
    };
  }

  handleUploadsChanged = (identifier) => (uploads) => {
    this.setState((prevState) => ({
      documents: {
        ...prevState.documents,
        [identifier]: uploads,
      },
      errors: {
        ...prevState.errors,
        [identifier]: false,
      },
    }));
  };

  handleUploadInProgress = (identifier) => (inProgress) => {
    this.setState((prevState) => ({
      documentsUploading: {
        ...prevState.documentsUploading,
        [identifier]: inProgress,
      },
    }));
  };

  handleValidation = () => {
    const documents = { ...this.state.documents };
    let errors = { ...this.state.errors };
    let errorMessages = { ...this.state.errorMessages };
    let formIsValid = true;

    Object.entries(documents).forEach(([documentId, uploads]) => {
      if (uploads.length === 0) {
        errors[documentId] = true;
        errorMessages[documentId] = "No file selected. Click below to select a file.";
        formIsValid = false;
      }
    });
    this.setState({ errors, errorMessages });
    return formIsValid;
  };

  submit = (event) => {
    event.preventDefault();

    if (!this.handleValidation()) {
      return;
    }

    this.setState({ submitting: true });

    let data = {
      uploaded_documents: [],
    };
    Object.entries(this.state.documents).forEach(([documentId, uploads]) => {
      uploads.forEach((upload) => {
        data.uploaded_documents.push({
          identifier: documentId,
          upload_id: upload.upload_id,
        });
      });
    });

    fetch(`/api/dashboard/doable/${this.props.doable.id}`, {
      method: "PUT",
      credentials: "same-origin",
      body: JSON.stringify(data),
    })
      .then((resp) => {
        if (resp.ok) {
          this.setState({
            submitting: false,
            submitted: true,
          });
        } else {
          if (resp.status === 409) {
            this.setState({
              submitting: false,
              error: "Documents already submitted",
              isDuplicateSubmission: true,
            });
          } else {
            Sentry.captureException(new Error(`Non-success response: ${resp.status} ${resp.statusText}`), {
              tags: {
                action: "DocumentUploadDoableSubmission",
                caseRef: this.props.caseRef,
                doableId: this.props.doable.id,
              },
            });
            this.setState({
              submitting: false,
              error: "Failed to submit documents",
            });
          }
        }
      })
      .catch((err) => {
        Sentry.captureException(err, {
          tags: {
            action: "DocumentUploadDoableSubmission",
            caseRef: this.props.caseRef,
            doableId: this.props.doable.id,
          },
        });
        this.setState({
          submitting: false,
          error: "Failed to submit documents",
          isDuplicateSubmission: false,
        });
      });
  };

  returnToDoable = () => {
    this.setState({ error: null });
  };

  createContent(content) {
    return { __html: sanitiseHtml(content) };
  }

  render() {
    if (!this.props.doable) return null;

    const requestTitle = this.props.doable.title;
    const requestDescription = this.props.doable.description;
    const documents = this.props.doable.configuration.documents;

    const { error, submitted } = this.state;

    const buttonDisabled = this.state.submitting || Object.values(this.state.documentsUploading).some((v) => v);

    const multipleDocuments = documents.some((doc) => doc.allow_multiple_files) || documents.length > 1;
    const documentsText = multipleDocuments ? "documents" : "document";

    if (error) {
      return (
        <React.Fragment>
          <ScrollToTop />
          <Card title="Sorry, that didn’t work" removeTopPadding headingSize="2">
            {this.state.isDuplicateSubmission ? (
              <>
                <p>You've already uploaded documents for this request.</p>
                <Action small onClick={this.props.returnToDashboard}>
                  Take me back to my dashboard
                </Action>
              </>
            ) : (
              <>
                <p>We're having trouble uploading your {documentsText} at the moment.</p>
                <Action small onClick={this.returnToDoable}>
                  Take me back to try again
                </Action>
              </>
            )}
          </Card>
        </React.Fragment>
      );
    }

    if (submitted) {
      return (
        <React.Fragment>
          <ScrollToTop />
          <Confirmation title={`Thanks! We’ve got your ${documentsText}`}>
            <p>As soon as we need anything else we’ll drop you an email with a link to your dashboard.</p>
            <Action small onClick={this.props.returnToDashboard}>
              Take me back to my dashboard
            </Action>
          </Confirmation>
          <Image src={imageOfficeWorking} fullWidth={true} />
        </React.Fragment>
      );
    } else {
      return (
        <React.Fragment>
          <ScrollToTop />
          <PageHeader>
            Upload {multipleDocuments ? "" : " a"} {documentsText}
          </PageHeader>
          <Card title={requestTitle}>
            {requestDescription && (
              <QuestionDescription dangerouslySetInnerHTML={this.createContent(requestDescription)} />
            )}
            {documents.map((doc) => (
              <FileUploadQuestion
                key={doc.identifier}
                question={doc.display_name}
                multiple={!!doc.allow_multiple_files}
                caseRef={this.props.caseRef}
                doableId={this.props.doable.id}
                defaultUploads={this.state.documents[doc.identifier]}
                onUploadsChanged={this.handleUploadsChanged(doc.identifier)}
                onUploadInProgress={this.handleUploadInProgress(doc.identifier)}
                validationError={this.state.errors[doc.identifier] ? this.state.errorMessages[doc.identifier] : null}
              />
            ))}
            <Action small disabled={buttonDisabled} onClick={this.submit}>
              Submit your {documentsText}
            </Action>
          </Card>
        </React.Fragment>
      );
    }
  }
}

export default DocumentUploadDoable;
